import {
  CHART_PART,
  JSZip,
  WorkbookCells,
  decodeXml,
  readValue,
  valueAxes,
} from "./preslinkChartAxis";

const SERIES_GROUP = /<c:(\w+Chart)>([\s\S]*?)<\/c:\1>/g;
const PLOTTED_SERIES = /<c:ser>([\s\S]*?)<\/c:ser>/g;
const ANY_SERIES = /<(c|c15):ser>[\s\S]*?<\/\1:ser>/g;
const LEGEND = /<c:legend>[\s\S]*?<\/c:legend>/;
const LEGEND_ENTRY = /<c:legendEntry>([\s\S]*?)<\/c:legendEntry>/g;
const SINGLE_CELL = /^(?:'(?:[^']|'')+'|[^!'()]+)!\$?[A-Z]{1,3}\$?\d+$/;
/** Legends of these list data points or trendlines, not one entry per series. */
const NON_SERIES_LEGEND =
  /<c:(?:pie|pie3D|doughnut|ofPie|surface|surface3D)Chart>|<c:varyColors val="(?:1|true)"\s*\/>|<c:trendline>/;
const EMPTY_RUN =
  /<a:r>\s*(<a:rPr\b[^>]*?(?:\/>|>[\s\S]*?<\/a:rPr>))?\s*<a:t\s*(?:\/>|><\/a:t>)\s*<\/a:r>/;

export interface ChartLabelRepair {
  chartPath: string;
  /** Legend positions now hidden, present only when the legend was rewritten. */
  hiddenLegendEntries?: number[];
  cellLabelFields: number;
}

interface LegendSeries {
  documentIndex: number;
  idx: string;
  order: number;
  hidden: boolean;
}

/** A one-point scatter series that marks a line's last value and carries its label. */
function isEndpoint(seriesXml: string): boolean {
  const formula = /<c:yVal>[\s\S]*?<c:f>([^<]*)<\/c:f>/.exec(seriesXml)?.[1];
  return formula !== undefined && SINGLE_CELL.test(decodeXml(formula));
}

/**
 * Series in legend order: chart groups as laid out, then plot order. Endpoints
 * and helper series whose every value lies outside the fixed value axis get no
 * legend entry.
 */
async function legendSeries(plotArea: string, cells: WorkbookCells): Promise<LegendSeries[]> {
  const axes = valueAxes(plotArea);
  const ordered: LegendSeries[] = [];
  let documentIndex = 0;
  for (const group of plotArea.matchAll(SERIES_GROUP)) {
    const body = group[2] ?? "";
    const axisIds = new Set(Array.from(body.matchAll(/<c:axId val="([^"]*)"/g), (match) => match[1]));
    const bounds = axes.find((axis) => axis.vertical && axisIds.has(axis.id))?.bounds;
    const inGroup: LegendSeries[] = [];
    for (const series of body.matchAll(PLOTTED_SERIES)) {
      const xml = series[1] ?? "";
      const values = /<c:(val|yVal)>([\s\S]*?)<\/c:\1>/.exec(xml)?.[2] ?? "";
      const formula = decodeXml(/<c:f>([^<]*)<\/c:f>/.exec(values)?.[1] ?? "");
      const endpoint = isEndpoint(xml);
      const numbers = bounds && formula ? await cells.numbersIn(formula) : undefined;
      const offPlot =
        !!bounds &&
        !!numbers?.length &&
        numbers.every((value) => value < bounds.min || value > bounds.max);
      inGroup.push({
        documentIndex: documentIndex++,
        idx: readValue(xml, "idx") ?? "",
        order: Number(readValue(xml, "order") ?? 0),
        hidden: endpoint || offPlot,
      });
    }
    ordered.push(...inGroup.sort((left, right) => left.order - right.order));
  }
  return ordered;
}

function legendHiding(legendXml: string, positions: number[]): string {
  const entries = positions
    .map((position) => `<c:legendEntry><c:idx val="${position}"/><c:delete val="1"/></c:legendEntry>`)
    .join("");
  const cleared = legendXml.replace(LEGEND_ENTRY, "");
  const legendPos = /<c:legendPos\b[^>]*\/>/.exec(cleared);
  const at = legendPos ? legendPos.index + legendPos[0].length : "<c:legend>".length;
  return cleared.slice(0, at) + entries + cleared.slice(at);
}

function fieldId(chartNumber: number, field: number): string {
  const suffix = (chartNumber * 4096 + field).toString(16).toUpperCase().padStart(12, "0");
  return `{00000000-0000-4000-8000-${suffix}}`;
}

async function repairChart(
  chartXml: string,
  chartPath: string,
  cells: WorkbookCells,
): Promise<{ xml: string; repair: ChartLabelRepair } | undefined> {
  const plotArea = /<c:plotArea>[\s\S]*<\/c:plotArea>/.exec(chartXml);
  if (!plotArea) return undefined;
  const chartNumber = Number(/chart(\d+)\.xml$/.exec(chartPath)?.[1] ?? 0);

  let cellLabelFields = 0;
  let plotXml = plotArea[0].replace(PLOTTED_SERIES, (series) =>
    isEndpoint(series) && series.includes("<c15:datalabelsRange>")
      ? series.replace(/<c:dLbl>[\s\S]*?<\/c:dLbl>/g, (label) => {
          if (
            !/<c15:showDataLabelsRange val="(?:1|true)"/.test(label) ||
            /<a:fld\b/.test(label) ||
            /<a:t>[^<]/.test(label) ||
            !EMPTY_RUN.test(label)
          ) {
            return label;
          }
          cellLabelFields += 1;
          const id = fieldId(chartNumber, cellLabelFields);
          return label.replace(
            EMPTY_RUN,
            (_run, runProperties: string | undefined) =>
              `<a:fld id="${id}" type="CELLRANGE">${runProperties ?? ""}<a:t>[CELLRANGE]</a:t></a:fld>`,
          );
        })
      : series,
  );

  const legend = LEGEND.exec(chartXml);
  let legendXml = legend?.[0];
  let hiddenLegendEntries: number[] | undefined;
  if (legend && !NON_SERIES_LEGEND.test(plotXml)) {
    const series = await legendSeries(plotXml, cells);
    const deleted = new Set(
      Array.from(legend[0].matchAll(LEGEND_ENTRY))
        .filter((entry) => /<c:delete val="(?:1|true)"/.test(entry[1] ?? ""))
        .map((entry) => readValue(entry[1] ?? "", "idx")),
    );
    const agrees = series.every(
      (entry, position) =>
        deleted.has(String(position)) === entry.hidden && deleted.has(entry.idx) === entry.hidden,
    );
    if (!agrees) {
      // With each series index equal to its legend position, readers that
      // match entries by index (Excel) and by position (Preslink) agree.
      const positions = new Map(series.map((entry, position) => [entry.documentIndex, position]));
      let plotted = 0;
      let filtered = series.length;
      plotXml = plotXml.replace(ANY_SERIES, (block, prefix: string) => {
        const idx = prefix === "c" ? positions.get(plotted++) : filtered++;
        return block.replace(/<c:idx val="[^"]*"\s*\/>/, () => `<c:idx val="${idx}"/>`);
      });
      hiddenLegendEntries = series.flatMap((entry, position) => (entry.hidden ? [position] : []));
      legendXml = legendHiding(legend[0], hiddenLegendEntries);
    }
  }

  if (cellLabelFields === 0 && !hiddenLegendEntries) return undefined;
  let xml = chartXml.slice(0, plotArea.index) + plotXml + chartXml.slice(plotArea.index + plotArea[0].length);
  if (legend && legendXml !== legend[0]) xml = xml.replace(legend[0], () => legendXml ?? legend[0]);
  return {
    xml,
    repair: { chartPath, ...(hiddenLegendEntries ? { hiddenLegendEntries } : {}), cellLabelFields },
  };
}

/**
 * ChartCreator numbers legend entries by series index, while the Preslink
 * renderer reads them as legend positions, and its multi-company workbooks
 * hide the wrong entries under either reading. Endpoint labels after the
 * first also lack their "value from cells" field, so they render blank.
 */
export async function repairChartCreatorLabels(
  workbookBase64: string,
): Promise<{ workbookBase64: string; repairs: ChartLabelRepair[] }> {
  const zip = await JSZip.loadAsync(workbookBase64, { base64: true });
  const cells = new WorkbookCells(zip);
  const repairs: ChartLabelRepair[] = [];
  for (const chartPath of Object.keys(zip.files).filter((path) => CHART_PART.test(path))) {
    const chartXml = await zip.file(chartPath)?.async("string");
    if (!chartXml) continue;
    const repaired = await repairChart(chartXml, chartPath, cells);
    if (!repaired) continue;
    zip.file(chartPath, repaired.xml);
    repairs.push(repaired.repair);
  }
  if (repairs.length === 0) return { workbookBase64, repairs };
  return {
    workbookBase64: await zip.generateAsync({ type: "base64", compression: "DEFLATE" }),
    repairs,
  };
}
